/**
 * Live Action Queue — a small overlay that shows what Koko is doing
 * right now while it choreographs a multi-step turn: each queued action
 * with a status (queued ▸ doing ✓ done), plus Pause / Stop the user can
 * hit at any time. Studio-only; mounted by StudioKokoBridge.
 *
 * Pure UI + a tiny item model. The bridge owns the real queue and calls
 * add()/setStatus(); the Pause/Stop buttons call back into the bridge.
 */

import "./styles/koko-queue.css";

export type QueueItemStatus = "queued" | "doing" | "done" | "failed" | "skipped";

interface QueueItem {
  id: number;
  label: string;
  status: QueueItemStatus;
}

interface LiveActionQueueCallbacks {
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
}

const STATUS_ICON: Record<QueueItemStatus, string> = {
  queued: "•",
  doing: "▸",
  done: "✓",
  failed: "!",
  skipped: "–",
};

const AUTO_HIDE_MS = 1800;

export class LiveActionQueue {
  private readonly callbacks: LiveActionQueueCallbacks;
  private root: HTMLDivElement | null = null;
  private listEl: HTMLUListElement | null = null;
  private titleEl: HTMLSpanElement | null = null;
  private pauseButton: HTMLButtonElement | null = null;
  private items: QueueItem[] = [];
  private nextId = 1;
  private paused = false;
  private hideTimer: number | null = null;

  constructor(callbacks: LiveActionQueueCallbacks) {
    this.callbacks = callbacks;
  }

  mount(parent: HTMLElement): void {
    if (this.root) return;
    const root = document.createElement("div");
    root.className = "koko-queue";
    root.dataset.kokoQueue = "true";
    root.hidden = true;

    const header = document.createElement("div");
    header.className = "koko-queue-header";
    const dot = document.createElement("span");
    dot.className = "koko-queue-dot";
    const title = document.createElement("span");
    title.className = "koko-queue-title";
    title.textContent = "Koko is working…";
    header.appendChild(dot);
    header.appendChild(title);
    this.titleEl = title;

    const list = document.createElement("ul");
    list.className = "koko-queue-list";
    this.listEl = list;

    const controls = document.createElement("div");
    controls.className = "koko-queue-controls";
    const pause = document.createElement("button");
    pause.type = "button";
    pause.className = "koko-queue-btn";
    pause.textContent = "Pause";
    pause.addEventListener("click", () => this.togglePause());
    this.pauseButton = pause;
    const stop = document.createElement("button");
    stop.type = "button";
    stop.className = "koko-queue-btn is-stop";
    stop.textContent = "Stop";
    stop.addEventListener("click", () => this.handleStop());
    controls.appendChild(pause);
    controls.appendChild(stop);

    root.appendChild(header);
    root.appendChild(list);
    root.appendChild(controls);
    parent.appendChild(root);
    this.root = root;
  }

  /** Register a new queued action. Returns its id. */
  add(label: string): number {
    this.cancelHide();
    this.paused = false;
    this.syncPauseLabel();
    if (this.titleEl) this.titleEl.textContent = "Koko is working…";
    const id = this.nextId++;
    this.items.push({ id, label, status: "queued" });
    this.show();
    this.render();
    return id;
  }

  setStatus(id: number, status: QueueItemStatus): void {
    const item = this.items.find((i) => i.id === id);
    if (!item) return;
    item.status = status;
    this.render();
    if (this.allTerminal()) this.scheduleHide();
  }

  /** Mark every non-finished item as skipped (used on Stop). */
  markStopped(): void {
    this.items.forEach((i) => {
      if (i.status === "queued" || i.status === "doing") i.status = "skipped";
    });
    if (this.titleEl) this.titleEl.textContent = "Stopped";
    this.render();
    this.scheduleHide();
  }

  clear(): void {
    this.items = [];
    this.render();
    this.hideNow();
  }

  // ------------------------------------------------------------------

  private togglePause(): void {
    this.paused = !this.paused;
    this.syncPauseLabel();
    if (this.titleEl) {
      this.titleEl.textContent = this.paused ? "Paused" : "Koko is working…";
    }
    if (this.paused) {
      this.cancelHide();
      this.callbacks.onPause();
    } else {
      this.callbacks.onResume();
    }
  }

  private handleStop(): void {
    this.callbacks.onStop();
    this.markStopped();
  }

  private syncPauseLabel(): void {
    if (this.pauseButton) this.pauseButton.textContent = this.paused ? "Resume" : "Pause";
  }

  private allTerminal(): boolean {
    return (
      this.items.length > 0 &&
      this.items.every(
        (i) => i.status === "done" || i.status === "failed" || i.status === "skipped",
      )
    );
  }

  private render(): void {
    if (!this.listEl) return;
    this.listEl.replaceChildren();
    this.items.forEach((item) => {
      const li = document.createElement("li");
      li.className = `koko-queue-item is-${item.status}`;
      const icon = document.createElement("span");
      icon.className = "koko-queue-icon";
      icon.textContent = STATUS_ICON[item.status];
      const label = document.createElement("span");
      label.className = "koko-queue-label";
      label.textContent = item.label;
      li.appendChild(icon);
      li.appendChild(label);
      this.listEl!.appendChild(li);
    });
  }

  private show(): void {
    if (this.root) this.root.hidden = false;
  }

  private scheduleHide(): void {
    this.cancelHide();
    this.hideTimer = window.setTimeout(() => this.clear(), AUTO_HIDE_MS);
  }

  private cancelHide(): void {
    if (this.hideTimer !== null) {
      window.clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  private hideNow(): void {
    this.cancelHide();
    if (this.root) this.root.hidden = true;
  }
}
